import React, { useState } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Building2, User, Wrench, ChevronRight } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useForm } from '../hooks/useForm'
import { useToast } from '../components/Toast'
import authService from '../services/auth'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import Select from '../components/ui/Select'

const RegisterPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const { success, error: showError } = useToast()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [currentStep, setCurrentStep] = useState(1) // 1 = Empresa, 2 = Administrador

  // Redirecionar se já estiver logado
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    getFieldProps,
    setFieldError
  } = useForm({
    initialValues: {
      // Dados da Empresa
      empresaNome: '',
      empresaEmail: '',
      empresaNif: '',
      empresaTelefone: '',
      empresaMorada: '',
      plano: 'BASICO',
      // Dados do Administrador
      nome: '',
      email: '',
      password: '',
      confirmPassword: ''
    },
    validationRules: {
      empresaNome: {
        required: 'Nome da empresa é obrigatório',
        minLength: { value: 3, message: 'Nome deve ter pelo menos 3 caracteres' }
      },
      empresaEmail: {
        required: 'Email da empresa é obrigatório',
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: 'Email inválido'
        }
      },
      empresaNif: {
        required: 'NIF é obrigatório',
        minLength: { value: 9, message: 'NIF deve ter pelo menos 9 caracteres' }
      },
      nome: {
        required: 'Nome é obrigatório',
        minLength: { value: 2, message: 'Nome deve ter pelo menos 2 caracteres' }
      },
      email: {
        required: 'Email é obrigatório',
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: 'Email inválido'
        }
      },
      password: {
        required: 'Senha é obrigatória',
        validate: (value: string) => {
          const validation = authService.validatePasswordStrength(value)
          return validation.isValid || validation.errors[0]
        }
      },
      confirmPassword: {
        required: 'Confirmação de senha é obrigatória',
        validate: (value: string) => {
          return value === values.password || 'As senhas não coincidem'
        }
      }
    },
    onSubmit: async (formValues) => {
      try {
        // Verificar se as senhas coincidem
        if (formValues.password !== formValues.confirmPassword) {
          setFieldError('confirmPassword', 'As senhas não coincidem')
          return
        }

        const { confirmPassword, ...registerData } = formValues
        await authService.register(registerData)

        success('Empresa e conta criadas com sucesso! Bem-vindo ao NexaMakers OS.')
        // O AuthContext já vai redirecionar automaticamente
      } catch (err: any) {
        showError('Erro ao criar conta', err.message)
      }
    }
  })

  const planoOptions = [
    { value: 'BASICO', label: 'Básico - Todas funcionalidades (exceto WhatsApp e IA)' },
    { value: 'PROFISSIONAL', label: 'Profissional - Sem restrições (tudo incluído)' }
  ]

  const getPasswordStrength = () => {
    if (!values.password) return null

    const validation = authService.validatePasswordStrength(values.password)
    const colors = ['red', 'orange', 'yellow', 'blue', 'green']
    const labels = ['Muito Fraca', 'Fraca', 'Razoável', 'Boa', 'Forte']

    return {
      score: validation.score,
      color: colors[validation.score] || 'red',
      label: labels[validation.score] || 'Muito Fraca',
      isValid: validation.isValid
    }
  }

  const passwordStrength = getPasswordStrength()

  const goToNextStep = () => {
    // Validar campos da empresa antes de avançar
    const empresaFields = ['empresaNome', 'empresaEmail', 'empresaNif']
    const hasErrors = empresaFields.some(field => errors[field])
    const hasEmptyFields = empresaFields.some(field => !values[field as keyof typeof values])

    if (!hasErrors && !hasEmptyFields) {
      setCurrentStep(2)
    } else {
      showError('Preencha todos os campos obrigatórios da empresa')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center bg-primary-600 rounded-full">
            <Wrench className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Registrar Nova Empresa
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Crie sua conta e comece a gerenciar ordens de serviço
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              <Building2 className="h-4 w-4" />
            </div>
            <span className={`text-sm font-medium ${
              currentStep >= 1 ? 'text-primary-600' : 'text-gray-400'
            }`}>
              Dados da Empresa
            </span>
          </div>

          <ChevronRight className="h-5 w-5 text-gray-400" />

          <div className="flex items-center space-x-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              <User className="h-4 w-4" />
            </div>
            <span className={`text-sm font-medium ${
              currentStep >= 2 ? 'text-primary-600' : 'text-gray-400'
            }`}>
              Administrador
            </span>
          </div>
        </div>

        {/* Formulário */}
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* ETAPA 1: Dados da Empresa */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Building2 className="h-5 w-5 mr-2 text-primary-600" />
                    Dados da Empresa
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Informações sobre a sua empresa
                  </p>
                </div>

                <Input
                  label="Nome da Empresa"
                  type="text"
                  placeholder="Ex: Oficina NexaMakers Lda"
                  required
                  {...getFieldProps('empresaNome')}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Email da Empresa"
                    type="email"
                    placeholder="contato@empresa.com"
                    required
                    {...getFieldProps('empresaEmail')}
                  />

                  <Input
                    label="NIF"
                    type="text"
                    placeholder="123456789"
                    required
                    {...getFieldProps('empresaNif')}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Telefone"
                    type="tel"
                    placeholder="+351 912 345 678"
                    {...getFieldProps('empresaTelefone')}
                  />

                  <Select
                    label="Plano"
                    options={planoOptions}
                    required
                    {...getFieldProps('plano')}
                  />
                </div>

                <Input
                  label="Morada (Opcional)"
                  type="text"
                  placeholder="Rua, Número, Cidade"
                  {...getFieldProps('empresaMorada')}
                />

                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={goToNextStep}
                  rightIcon={<ChevronRight className="h-4 w-4" />}
                >
                  Continuar
                </Button>
              </div>
            )}

            {/* ETAPA 2: Dados do Administrador */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <User className="h-5 w-5 mr-2 text-primary-600" />
                    Conta do Administrador
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Crie sua conta de administrador
                  </p>
                </div>

                <Input
                  label="Nome Completo"
                  type="text"
                  autoComplete="name"
                  placeholder="Seu nome completo"
                  required
                  {...getFieldProps('nome')}
                />

                <Input
                  label="Email"
                  type="email"
                  autoComplete="email"
                  placeholder="seu@email.com"
                  required
                  {...getFieldProps('email')}
                />

                <div className="space-y-2">
                  <Input
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Sua senha"
                    required
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    }
                    {...getFieldProps('password')}
                  />

                  {/* Indicador de força da senha */}
                  {passwordStrength && values.password && (
                    <div className="space-y-2">
                      <div className="flex space-x-1">
                        {[...Array(5)].map((_, index) => (
                          <div
                            key={index}
                            className={`h-2 flex-1 rounded ${
                              index < passwordStrength.score
                                ? `bg-${passwordStrength.color}-500`
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-xs text-${passwordStrength.color}-600`}>
                        Força da senha: {passwordStrength.label}
                      </p>
                    </div>
                  )}
                </div>

                <Input
                  label="Confirmar Senha"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Confirme sua senha"
                  required
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                  {...getFieldProps('confirmPassword')}
                />

                <div className="flex space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    fullWidth
                    onClick={() => setCurrentStep(1)}
                  >
                    Voltar
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    isLoading={isSubmitting}
                  >
                    Criar Conta
                  </Button>
                </div>
              </div>
            )}
          </form>

          {/* Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Já tem uma conta?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Fazer login
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>© 2024 NexaMakers. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
